import { css } from '@linaria/core'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { RouteObject } from 'react-router'
import { useLoaderData, useNavigation } from 'react-router-dom'
import { Spinner } from '#components/Spinner'
import { InlineErrorDisplay } from '#features/ErrorHandling/ErrorDisplay'
import { type AuthRouteLoaderData, authRouteLoader } from '#features/User/authRouteLoader'
import { ForbiddenPage } from '#features/User/ForbiddenPage'
import { type InjectTokenLoaderData, injectTokenLoader } from '#features/User/injectTokenLoader'
import { type LoginRouteLoaderData, loginRouteLoader } from '#features/User/loginRouteLoader'
import { SignedOutPage } from '#features/User/SignedOutPage'

const pageContentClass = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow-x: hidden;
  padding-top: 60px;
  padding-bottom: 60px;
`

// biome-ignore lint/style/useComponentExportOnlyModules: can be ignored
const AuthFlowLoader = ({ pageHeader, loadingMessage }: { pageHeader?: ReactNode; loadingMessage?: string }) => {
  const { state } = useNavigation()

  return (
    <>
      {pageHeader}
      <div className={pageContentClass}>
        {state === 'loading' && 'asassaasasassa'}
        <Spinner size="xl" subtitle={loadingMessage} />
      </div>
    </>
  )
}

// biome-ignore lint/style/useComponentExportOnlyModules: can be ignored
const AuthFlowPage = ({
  pageHeader,
  title,
  busyMessage,
  error,
  children,
}: {
  pageHeader?: ReactNode
  loading?: boolean
  error?: unknown
  title?: string
  busyMessage?: string
  children?: ReactNode
}) => {
  return (
    <>
      {pageHeader}
      <div className={pageContentClass}>
        {!!error && <InlineErrorDisplay error={error} title={title} />}
        {!error && <Spinner size="xl" subtitle={busyMessage} />}
        {children}
      </div>
    </>
  )
}

// biome-ignore lint/style/useComponentExportOnlyModules: can be ignore
const LoginLoader = ({ pageHeader }: { pageHeader?: ReactNode }) => {
  const { t } = useTranslation('shared')
  return <AuthFlowLoader pageHeader={pageHeader} loadingMessage={t('auth.login.busy-message', 'Logging in…')} />
}

// biome-ignore lint/style/useComponentExportOnlyModules: can be ignored
const LoginRenderer = ({ pageHeader }: { pageHeader?: ReactNode }) => {
  const { isRedirecting, error } = useLoaderData<LoginRouteLoaderData>()
  const { t } = useTranslation('shared')
  const isLoading = isRedirecting || !error

  return (
    <AuthFlowPage
      error={error}
      pageHeader={pageHeader}
      loading={isLoading}
      busyMessage={
        isLoading ? t('auth.login.redirect-to-auth-provider', 'Redirecting to auth provider…') : t('auth.login.busy-message', 'Logging in…')
      }
      title={t('auth.login.error-message', 'An error occurred while logging in.')}
    />
  )
}

// biome-ignore lint/style/useComponentExportOnlyModules: can be ignored
const InjectTokenLoader = ({ pageHeader }: { pageHeader?: ReactNode }) => {
  const { t } = useTranslation('shared')
  return <AuthFlowLoader pageHeader={pageHeader} loadingMessage={t('auth.inject-token.busy-message', 'Injecting token…')} />
}

// biome-ignore lint/style/useComponentExportOnlyModules: can be ignored
const InjectTokenRenderer = ({ pageHeader }: { pageHeader?: ReactNode }) => {
  const { error } = useLoaderData<InjectTokenLoaderData>()
  const { t } = useTranslation('shared')

  return (
    <AuthFlowPage
      error={error}
      pageHeader={pageHeader}
      loading={!error}
      busyMessage={t('auth.inject-token.busy-message', 'Injecting token…')}
      title={t('auth.inject-token.error-message', 'An error occurred while injecting the token.')}
    />
  )
}

// biome-ignore lint/style/useComponentExportOnlyModules: can be ignored
const AuthLoader = ({ pageHeader }: { pageHeader?: ReactNode }) => {
  const { t } = useTranslation('shared')
  return <AuthFlowLoader pageHeader={pageHeader} loadingMessage={t('auth.auth.busy-message', 'Authenticating…')} />
}

// biome-ignore lint/style/useComponentExportOnlyModules: can be ignored
const AuthRenderer = ({ pageHeader }: { pageHeader?: ReactNode }) => {
  const { error } = useLoaderData<AuthRouteLoaderData>()
  const { t } = useTranslation('shared')

  return (
    <AuthFlowPage
      error={error}
      pageHeader={pageHeader}
      loading={!error}
      busyMessage={t('auth.auth.busy-message', 'Authenticating…')}
      title={t('auth.auth.error-message', 'An error occurred while authenticating.')}
    />
  )
}

export const buildAuthRouteFragments = (pageHeader?: ReactNode): RouteObject[] => [
  {
    path: 'auth',
    loader: authRouteLoader,
    element: <AuthRenderer pageHeader={pageHeader} />,
    shouldRevalidate: () => true,
    hydrateFallbackElement: <AuthLoader pageHeader={pageHeader} />,
  },
  {
    path: 'forbidden',
    element: <ForbiddenPage />,
  },
  {
    path: 'login',
    loader: loginRouteLoader,
    element: <LoginRenderer pageHeader={pageHeader} />,
    shouldRevalidate: () => true,
    hydrateFallbackElement: <LoginLoader pageHeader={pageHeader} />,
  },
  {
    path: '__inject_auth',
    loader: injectTokenLoader,
    element: <InjectTokenRenderer pageHeader={pageHeader} />,
    shouldRevalidate: () => true,
    hydrateFallbackElement: <InjectTokenLoader pageHeader={pageHeader} />,
  },
  {
    path: 'signed-out',
    element: (
      <>
        {pageHeader}
        <SignedOutPage />
      </>
    ),
  },
]

export const authRouteMatcher = /\/(login|auth|signed-out|__inject_auth)/
